import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { stagger } from '../utils/format';

/** How a self-guided tour works, in three numbered steps (Paper 11 HowItWorks). */
const STEP =
  'flex [flex:1_1_0] flex-col gap-4 border-t border-t-[--q-border] pt-6 max-[767px]:flex-row max-[767px]:py-5 max-[767px]:last:border-b max-[767px]:last:border-b-[--q-border]';
const STEP_N =
  'font-heading text-[40px] font-light leading-10 tracking-[--q-tracking-tight] text-[--q-primary-on-paper] max-[767px]:flex-[0_0_44px] max-[767px]:text-[28px] max-[767px]:leading-8';
const STEP_T = 'flex flex-col gap-4 max-[767px]:gap-1.5';
const STEP_TITLE =
  'm-0 font-heading text-[28px] font-normal leading-[34px] tracking-[--q-tracking-tight] [&_p]:m-0 max-[767px]:text-[22px] max-[767px]:leading-7';
const STEP_TEXT =
  'm-0 max-w-[340px] text-base leading-6 text-muted-foreground [&_p]:m-0 max-[767px]:text-[15px] max-[767px]:leading-[22px]';

export function TourSteps() {
  return (
    <section className="section tsteps">
      <div className="flex max-w-[640px] flex-col gap-3.5 [&_.h2_p]:m-0" data-reveal>
        <RichTextContent path="toursteps.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="toursteps.title" as="h2" className="h2" />
      </div>
      <ol className="m-0 mt-12 flex list-none gap-8 p-0 max-[991px]:flex-col max-[991px]:gap-0 max-[767px]:mt-7">
        <li className={STEP} data-reveal style={stagger(0)}>
          <span className={STEP_N}>01</span>
          <span className={STEP_T}>
            <RichTextContent path="toursteps.step1_title" as="h3" className={STEP_TITLE} />
            <RichTextContent path="toursteps.step1_text" as="p" className={STEP_TEXT} />
          </span>
        </li>
        <li className={STEP} data-reveal style={stagger(1)}>
          <span className={STEP_N}>02</span>
          <span className={STEP_T}>
            <RichTextContent path="toursteps.step2_title" as="h3" className={STEP_TITLE} />
            <RichTextContent path="toursteps.step2_text" as="p" className={STEP_TEXT} />
          </span>
        </li>
        <li className={STEP} data-reveal style={stagger(2)}>
          <span className={STEP_N}>03</span>
          <span className={STEP_T}>
            <RichTextContent path="toursteps.step3_title" as="h3" className={STEP_TITLE} />
            <RichTextContent path="toursteps.step3_text" as="p" className={STEP_TEXT} />
          </span>
        </li>
      </ol>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourSteps',
  displayName: 'How Touring Works',
  tokens: {
    'toursteps.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>How it works</p>' },
    'toursteps.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Three steps. Then you’re in.</p>' },
    'toursteps.step1_title': { label: 'Step 1 · Title', type: 'richtext', defaultValue: '<p>Choose a time</p>' },
    'toursteps.step1_text': { label: 'Step 1 · Text', type: 'richtext', defaultValue: '<p>Select a day and a time above.</p>' },
    'toursteps.step2_title': { label: 'Step 2 · Title', type: 'richtext', defaultValue: '<p>Get your access</p>' },
    'toursteps.step2_text': { label: 'Step 2 · Text', type: 'richtext', defaultValue: '<p>Your entry details arrive before you come.</p>' },
    'toursteps.step3_title': { label: 'Step 3 · Title', type: 'richtext', defaultValue: '<p>Explore</p>' },
    'toursteps.step3_text': { label: 'Step 3 · Text', type: 'richtext', defaultValue: '<p>Walk the home and the community at your own pace.</p>' },
  },
};

(TourSteps as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourSteps;
